// Home.jsx
import React from 'react';
import { useLocation, useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../resource/defaults.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareListPageMeta } from '../resource/prepareListPageMeta.js';
import UniversalContainer from '../component/UniversalContainer.jsx';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({ context, params, request }) => {
  const log = legacySSRLog.extend('Home loader()');
  const errorLog = legacySSRError.extend('Home loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    const state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData });

    // Get pathname to determine which page we're on
    const url = new URL(request.url);
    const pathname = url.pathname;

    // Determine SEO keys based on pathname
    // Note: Home page uses 'seo-explore-*' keys to match Next.js implementation
    let titleKey = 'seo-explore-title';
    let descriptionKey = 'seo-explore-description';
    let keywordsKey = 'seo-explore-keywords';
    let canonicalPath = '/';
    let jsonLdType = 'WebSite'; // Home page uses WebSite schema
    let robots = []; // Default: allow indexing

    if (pathname === '/video') {
      titleKey = 'seo_video_title';
      descriptionKey = 'seo_video_description';
      keywordsKey = 'seo_video_keywords';
      canonicalPath = '/video';
      jsonLdType = 'ItemList';
    } else if (pathname === '/livestream') {
      titleKey = 'seo_livestream_title';
      descriptionKey = 'seo_livestream_description';
      keywordsKey = 'seo_livestream_keywords';
      canonicalPath = '/livestream';
      jsonLdType = 'ItemList';
    } else if (pathname === '/following') {
      titleKey = 'seo-explore-title';
      descriptionKey = 'seo-explore-description';
      keywordsKey = 'seo-explore-keywords';
      canonicalPath = '/following';
      jsonLdType = 'ItemList';
      robots = ['noindex', 'nofollow']; // Following page should not be indexed
    }

    const availableLanguages = getRemoteConfigData(state, SUPPORTED_LANGUAGES);
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareListPageMeta({
      state,
      titleKey,
      descriptionKey,
      keywordsKey,
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      jsonLdType,
      robots,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const HomeRoute = () => {
  const { pathname } = useLocation();
  const isFollowing = pathname === '/following';

  const { data, error, meta = EMPTY_ARRAY } = useLoaderData() || EMPTY_OBJECT;

  useSSRDataMerge({
    componentName: 'Home',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalContainer
        filename="Home"
        shouldEnableSentrySpan={!isFollowing}
      />
    </>
  );
};

export default HomeRoute;
